import { AlertTriangle, Sparkles } from "lucide-react";
import type { ReorderFieldResolution } from "@shared-core/data/api-types";

/**
 * Per-field confidence chip for the reorder three-tier ladder (brief §16):
 *
 * - `"preset"` — nothing rendered. Indistinguishable from a manual pick, by
 *   design (§16, decision 2).
 * - `"custom"` — a subtle badge: the control is pre-populated but it is
 *   worth knowing the value came from the source job, not typed fresh.
 * - `"unresolved"` — a "was: X" chip the CSR must act on: the field carries
 *   no default and blocks price-preview until they choose a current value.
 */
export function ReorderFieldChip({
  resolution,
}: {
  resolution: ReorderFieldResolution | undefined;
}) {
  if (!resolution || resolution.tier === "preset") return null;

  if (resolution.tier === "custom") {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-[color:var(--color-primary)]/10 px-2 py-0.5 text-xs text-[color:var(--color-primary)]">
        <Sparkles className="size-3" />
        Pre-filled from reorder
      </span>
    );
  }

  // "unresolved"
  return (
    <span className="inline-flex items-center gap-1 rounded-full border border-amber-300 bg-amber-50 px-2 py-0.5 text-xs text-amber-900">
      <AlertTriangle className="size-3" />
      Was: {resolution.source_value ?? "unknown"} — choose a current value
    </span>
  );
}
